import defaultThumbnail from "@assets/thumbnails/project-thumbs/project-thumbnail_default.svg";
import { PROJECTS_SORT_BY_MAP } from "@components/projects/projects-sort-constants";
import { ProjectLaunchTarget } from "@custom-types/sdb-company-types";
import { commonStrings } from "@src/constants/common-strings";
import { runtimeConfig } from "@src/runtime-config";
import { FaroCoreApiUtils } from "@stellar/core-api-client";
import { DateTimeUtils, UiUtils, UtilsTypes, WebEditorUtils } from "@stellar/utils";
import { formatAreaUnit } from "@utils/data-display";
import { ProjectEvents } from "@utils/track-event/track-event-list";
import { formatStorageSize, getStorageUnit } from "@utils/ui-utils";
import { convertAreaAmount } from "@utils/unit-utils";
import { getPrettyName } from "@utils/user-utils";

/** Default project thumbnail image */
export const defaultProjectThumbnail = defaultThumbnail;

/** Defines the minimum length for the project name */
export const MIN_PROJECT_NAME_LENGTH = 1;

/**
 * Max length of the project name forwarded to Sphere Vault via the deep-link
 * `projectName` query param. Keeps the URL comfortably within length limits even
 * with several params
 */
const MAX_VAULT_PROJECT_NAME_LENGTH = 60;

/** Defines the default project size area in sqft to be used when not provided by the the user */
export const DEFAULT_PROJECT_AREA = 1;

/** Defines the minimum number of characters to start a project search */
export const MIN_CHARACTERS_FOR_PROJECT_SEARCH = 3;

/** Specifies the target attribute value for opening the project. */
export const OPEN_PROJECT_TARGET_ATTRIBUTE = "_self";

/** Specifies the target attribute value for opening other elements annotations, etc. */
export const OPEN_OTHERS_TARGET_ATTRIBUTE = "_blank";

/**
 * Sphere Viewer deep link format:
 * https://faro01.atlassian.net/wiki/spaces/SWEB/pages/3717529850/Sphere+Viewer+Deep+Link+Format
 */

/**
 * @returns the query parameters required to build a valid Sphere Viewer deep link that
 * opens the project and shows the element specified by the ID of the element.
 */
function getSphereViewerUrlParams({
  lookAtId,
  id
}) {
  const params = new URLSearchParams();
  if (lookAtId) {
    params.append("lookAtId", lookAtId);
  }
  if (id) {
    params.append("id", id);
  }
  return params;
}
export function getWebEditorUrl(projectId, mode) {
  return new URL(WebEditorUtils.generateWebEditorUrl({
    baseUrl: runtimeConfig.urls.hbWebEditorUrl,
    projectId,
    mode: mode ?? "editor"
  }));
}
/**
 * Returns the URL to open a project in the Sphere Viewer.
 */
export function getSphereViewerUrl({
  projectId,
  lookAtId,
  id,
  additionalParams
}) {
  const url = new URL(`project/${projectId}`, runtimeConfig.urls.sphereViewerUrl);
  const params = getSphereViewerUrlParams({
    lookAtId,
    id
  });
  if (additionalParams) {
    for (const [key, value] of additionalParams) {
      params.append(key, value);
    }
  }
  url.search = params.toString();
  return url;
}

/**
 * Returns the URL to open the Data Management editor in the Sphere Viewer.
 */
export function getDataManagementUrl({
  projectId,
  shouldEnableDev,
  shouldEnableAlpha,
  shouldEnableBeta,
  shouldShowUpload,
  shouldShowPublish
}) {
  const url = new URL(`/data-management/project/${projectId}`, runtimeConfig.urls.sphereViewerUrl);
  const params = new URLSearchParams();
  if (shouldEnableDev) {
    params.append("enableDev", "true");
  }
  if (shouldEnableAlpha) {
    params.append("enableAlpha", "true");
  }
  if (shouldEnableBeta) {
    params.append("enableBeta", "true");
  }
  if (shouldShowUpload) {
    params.append("showUpload", "true");
  }
  if (shouldShowPublish) {
    params.append("showPublish", "true");
  }
  url.search = params.toString();
  return url;
}
/**
 * Gets the props related to the creation and last update date of a project to be used in tracking events.
 */
export function getTrackProjectDateProps({
  modifiedAt,
  createdAt
}) {
  const eventProps = {};
  if (modifiedAt) {
    eventProps.lastUpdateAge = DateTimeUtils.getAge(modifiedAt);
    eventProps.lastUpdateAgeRange = DateTimeUtils.getDisplayAge(eventProps.lastUpdateAge);
  }
  if (createdAt) {
    eventProps.creationAge = DateTimeUtils.getAge(createdAt);
    eventProps.creationAgeRange = DateTimeUtils.getDisplayAge(eventProps.creationAge);
  }
  return eventProps;
}

/**
 * Tracks whenever a user wants to open a project in the Sphere Viewer or WebEditor.
 * This function does not open the project, it only tracks the event.
 *
 * @param openFrom
 */
export async function trackOpenProject({
  openFrom,
  numberOfMembers,
  openTarget = ProjectLaunchTarget.sphereViewer,
  clickType,
  trackAsyncEvent,
  modifiedAt,
  createdAt,
  accessLevel
}) {
  const shouldOpenInSV = openTarget === ProjectLaunchTarget.sphereViewer;
  const eventProps = {
    openFrom,
    ...getTrackProjectDateProps({
      modifiedAt,
      createdAt
    })
  };
  if (numberOfMembers) {
    eventProps.numberOfMembers = numberOfMembers;
  }
  if (shouldOpenInSV) {
    eventProps.openTarget = ProjectLaunchTarget.sphereViewer;
  } else {
    eventProps.openTarget = ProjectLaunchTarget.webEditor;
  }
  if (clickType) {
    eventProps.clickType = clickType;
  }
  if (accessLevel) {
    eventProps.accessLevel = accessLevel;
  }
  await trackAsyncEvent({
    name: ProjectEvents.openProject,
    props: eventProps
  });
}
/**
 * Tracks when a user opens project details.
 * Generic helper that centralizes the openProjectDetails event tracking.
 * Each call site can pass its own specific props.
 */
export function trackOpenProjectDetails({
  projectId,
  trackEvent,
  props
}) {
  trackEvent({
    name: ProjectEvents.openProjectDetails,
    props: {
      projectId,
      ...props
    }
  });
}

/** Entry points from which the project location actions can be triggered */

/** Props for tracking the opening of the project location editor dialog */

/**
 * Tracks when the user opens the project location editor dialog
 */
export function trackOpenProjectLocationEditor({
  trackEvent,
  source
}) {
  trackEvent({
    name: ProjectEvents.openProjectLocationEditor,
    props: {
      source
    }
  });
}

/** Props for tracking a navigation to a project's location on the map */

/**
 * Tracks when the user navigates the map to a project's location
 */
export function trackGoToProjectLocation({
  trackEvent,
  source
}) {
  trackEvent({
    name: ProjectEvents.goToProjectLocation,
    props: {
      source
    }
  });
}

/** How the user provided a project address, reported in the project address tracking events */

/**
 * A project without an address is stored either as an empty string or as a dash placeholder.
 *
 * @returns Whether the given project address should be treated as not set.
 * @param address The project address to check.
 */
export function isProjectAddressUnset(address) {
  const trimmedAddress = address.trim();
  return trimmedAddress.length === 0 || trimmedAddress === UiUtils.DASH;
}

/**
 * A postal address is only available when the address came from a geocoded source,
 * either an address suggestion or a reverse geocoded map position.
 *
 * @returns How the user provided the given project address.
 */
export function getProjectAddressType({
  address,
  postalAddress,
  emptyAddressType
}) {
  if (isProjectAddressUnset(address)) {
    return emptyAddressType;
  }
  return postalAddress ? "geocoded" : "custom";
}

/**
 * Gets the URL to open the project for the provided target.
 */
export function getOpenProjectHref({
  openTarget,
  projectId,
  additionalParams
}) {
  return openTarget === ProjectLaunchTarget.sphereViewer ? getSphereViewerUrl({
    projectId,
    additionalParams
  }).href : getWebEditorUrl(projectId).href;
}

/**
 * Gets the display name for the provided target.
 */
export function getOpenProjectTargetName(target) {
  return target === ProjectLaunchTarget.sphereViewer ? commonStrings.SPHERE_XG : commonStrings.HOLOBUILDER_PRODUCT_NAME;
}

/**
 * Builds the label of an action that opens an entity in one of the viewer apps.
 * Workspaces that cannot open the WebEditor have a single target, so naming it is redundant.
 */
export function getOpenInTargetLabel({
  target,
  canOpenWebEditor,
  shouldOmitVerb = false
}) {
  if (!canOpenWebEditor) {
    return "Open";
  }
  const targetName = getOpenProjectTargetName(target);
  return shouldOmitVerb ? targetName : `Open in ${targetName}`;
}

/**
 * Opens a project in one of the viewer apps (Sphere Viewer or WebEditor). Which one is
 * dependant on the openTarget prop.
 */
export async function openProjectInViewer({
  project,
  openFrom,
  numberOfMembers,
  openTarget = ProjectLaunchTarget.sphereViewer,
  lookAtId,
  id,
  clickType,
  trackAsyncEvent
}) {
  const shouldOpenInSV = openTarget === ProjectLaunchTarget.sphereViewer;
  const projectId = project.id;
  await trackOpenProject({
    openFrom,
    numberOfMembers,
    openTarget,
    clickType,
    modifiedAt: project.modifiedAt,
    createdAt: project.createdAt,
    trackAsyncEvent
  });
  const targetUrl = shouldOpenInSV ? getSphereViewerUrl({
    projectId,
    lookAtId,
    id
  }) : getWebEditorUrl(projectId);
  window.location.assign(targetUrl.href);
}

/**
 * Converts to the simplified project archiving state from the more elaborated ArchivingState.
 *
 * @param archivingState Original archiving state, e.g. "ARCHIVED_DOWNLOADED"
 * @returns The simplified version of the archiving state, e.g. "active".
 */
export function getProjectArchivingState(archivingState) {
  switch (archivingState) {
    case "ARCHIVED":
    case "ARCHIVED_DOWNLOADED":
    case "ARCHIVED_READY_TO_DOWNLOAD":
      return UtilsTypes.ProjectArchivingState.archived;
    case "UNARCHIVED":
      return UtilsTypes.ProjectArchivingState.active;
    default:
      return archivingState;
  }
}

/**
 * Convert the readable project state to the API archiving state
 */
export function projectStateToApiArchivingState(projectArchivingState) {
  let archivingStates = ["UNARCHIVED"];
  if (projectArchivingState === UtilsTypes.ProjectArchivingState.archived) {
    archivingStates = ["ARCHIVED", "ARCHIVED_DOWNLOADED", "ARCHIVED_READY_TO_DOWNLOAD"];
  }
  return archivingStates;
}

/**
 * Returns the name of the group the project belongs to. The streamed projects only have the ID of
 * their group, so its name is looked up in the given names of the groups by their ID. Returns a
 * dash if the name is not known.
 */
export function getProjectGroupName(project, groupNamesById = new Map()) {
  if (project.group) {
    return project.group.name;
  }
  const groupName = project.groupId ? groupNamesById.get(project.groupId) : undefined;
  return groupName ?? UiUtils.DASH;
}

/**
 * Returns the id of the group the project belongs to. The streamed projects only have the ID.
 */
export function getProjectGroupId(project) {
  return project.group?.id ?? project.groupId;
}

/**
 * Returns true if a project is a demo.
 */
export function isProjectDemo(project) {
  const demoTag = "sys:demo";
  return project.tags.includes(demoTag);
}

/**
 * Gets the project manager for the provided project.
 *
 * @param project Project to extract its project manager from.
 * @returns The project manager, null otherwise.
 */
export function getProjectManager(project) {
  // TODO: we don't yet support UserAsManager in MemberTypes: https://faro01.atlassian.net/browse/ST-1721, ST-646
  return project.managers.projectManager ?? null;
}

/**
 * Gets the name of the project manager for the provided project.
 *
 * @param project Project to extract its project manager from.
 * @returns The project manager, a dash otherwise.
 */
export function getProjectManagerName(project) {
  return getPrettyName(getProjectManager(project)) || UiUtils.DASH;
}

/** Returns the project's client name */
export function getProjectClientName(project) {
  return project.clientName ?? UiUtils.DASH;
}

/** Returns the project assigned area, expressed in the given area unit */
export function getProjectArea(project, areaUnit) {
  if (!project.area) {
    return UiUtils.DASH;
  }
  const amount = convertAreaAmount({
    amount: project.area.amount,
    fromUnit: project.area.unit,
    toUnit: areaUnit
  });
  return `${Number(amount.toFixed(2))} ${formatAreaUnit(areaUnit)}`;
}

/** Returns the project size */
export function getProjectSize(project) {
  const size = project.storage?.size;
  if (!size) {
    return UiUtils.DASH;
  }
  const currentToDisplay = formatStorageSize(size).toFixed(2);
  const currentUnit = getStorageUnit(size);
  return `${currentToDisplay} ${currentUnit}`;
}

/** Returns the project access level, followed by its size when it has one, e.g. "Private • 1.20 GB" */
export function getProjectAccessAndSize(project) {
  const accessLevel = FaroCoreApiUtils.Project.AccessLevelDisplayNames[project.accessLevel];
  const size = getProjectSize(project);
  return size === UiUtils.DASH ? accessLevel : `${accessLevel} • ${size}`;
}

/**
 * Number of decimal places used when displaying GPS coordinates.
 * Five decimals is roughly 1 meter of precision, which is plenty for a location label.
 */
const DISPLAYED_COORDINATE_DECIMALS = 5;

/** Rounds a coordinate to a human-friendly precision, dropping trailing zeros. */
function roundCoordinate(value) {
  return Number(value.toFixed(DISPLAYED_COORDINATE_DECIMALS));
}

/**
 * Returns the project's human-readable address, or null when it is empty or only
 * contains dash placeholders (e.g. "-", "–", "—").
 */
export function getProjectAddress(project) {
  const address = project.address.trim();
  const isPlaceholderAddress = address === "" || /^[-–—]+$/.test(address);
  return isPlaceholderAddress ? null : address;
}

/**
 * @returns a short version of the project's address, with just the city and country code.
 * If the city or country code are not defined it returns null.
 * @param project Project entity
 */
export function getProjectAddressShort(project) {
  const city = project.postalAddress?.locality;
  const countryCode = project.postalAddress?.countryCode;
  if (!city || !countryCode) {
    return null;
  }
  return `${city}, ${countryCode}`;
}

/**
 * Returns the project's GPS coordinates rounded for display (e.g. "37.422, -122.084"),
 * or null when the project has no position.
 */
export function getProjectCoordinates(project) {
  if (!project.position) {
    return null;
  }
  const {
    lat,
    lng
  } = project.position;
  return `${roundCoordinate(lat)}, ${roundCoordinate(lng)}`;
}

/**
 * @returns the project's location and location tooltip to display.
 * A project is only considered to have a location when it has GPS coordinates.
 * The short version of the address is preferred over the full address. If none are present
 * then the location text defaults to the coordinates.
 * For the location tooltip, the full address is preferred. If full address is not present
 * then the tooltip also defaults to the coordinates.
 */
export function getProjectLocation(project) {
  const coordinates = getProjectCoordinates(project);
  if (!coordinates) {
    return {
      location: null,
      tooltip: null
    };
  }
  const location = getProjectAddressShort(project) || getProjectAddress(project) || coordinates;
  const tooltip = getProjectAddress(project) || coordinates;
  return {
    location,
    tooltip
  };
}

/**
 * @returns whether the project archiving state is "downloaded"
 * @param project Project entity
 */
export function isProjectDownloaded(project) {
  return project.archivingState === "ARCHIVED_DOWNLOADED";
}

/**
 * Archiving states for which a project can be opened in Sphere Vault for download.
 * Any archived project qualifies — Sphere Vault performs no prepare transition, so
 * plainly `ARCHIVED` projects are offered alongside ones already marked ready or
 * downloaded (a re-download is allowed). Keep this predicate as the single source
 * of truth so the set of states is easy to change later.
 *
 * @param archivingState Project archiving state
 */
export function isProjectReadyForVaultDownload(archivingState) {
  return archivingState === "ARCHIVED" || archivingState === "ARCHIVED_READY_TO_DOWNLOAD" || archivingState === "ARCHIVED_DOWNLOADED";
}

/**
 * Build the Sphere Vault downloader deep-link for a project, e.g.
 * `https://vault.holobuilder.com/?projectId=<id>&projectName=<name>&companyId=<id>`.
 * The Vault needs the ids (and, optionally, the display name):
 * - `projectId` to preselect the right project to download in the Vault.
 * - `projectName` used to create a human-readable download folder name. It's truncated (see
 *   {@link MAX_VAULT_PROJECT_NAME_LENGTH}) to not exceed URL length limits..
 * - `companyId` is passed so the Vault can use company scoped backend routes
 *
 * @param projectId The project to open in Sphere Vault
 * @param projectName Optional project display name to seed the download folder name
 * @param companyId Optional company id the project belongs to
 */
export function getSphereVaultProjectDownloadUrl(projectId, projectName, companyId) {
  const url = new URL(runtimeConfig.urls.sphereVaultUrl);
  url.searchParams.set("projectId", projectId);
  if (projectName) {
    url.searchParams.set("projectName", projectName.slice(0, MAX_VAULT_PROJECT_NAME_LENGTH));
  }
  if (companyId) {
    url.searchParams.set("companyId", companyId);
  }
  return url.toString();
}

/**
 * Track and open a project in Sphere Vault in a new tab.
 *
 * @param projectId The project to open in Sphere Vault
 * @param projectName Optional project display name to seed the download folder name
 * @param companyId Optional company id the project belongs to
 * @param trackEvent Tracking function from useTrackEvent
 */
export function openInSphereVault({
  projectId,
  projectName,
  companyId,
  trackEvent
}) {
  trackEvent({
    name: ProjectEvents.openProjectInSphereVault
  });
  window.open(getSphereVaultProjectDownloadUrl(projectId, projectName, companyId), "_blank", "noopener,noreferrer");
}

/**
 * @returns true if open project button should be hidden
 * @param project Project entity
 *
 * Open project button will be hidden if
 * - The project is archived and user has not the resource owner role
 */
export function shouldHideOpenProject(project) {
  if (!project) {
    return false;
  }
  const isProjectArchived = getProjectArchivingState(project.archivingState) === UtilsTypes.ProjectArchivingState.archived;
  return isProjectArchived && project.role !== "RESOURCE_OWNER";
}

/** Check whether a feature exist in a project or not */
export function isFeatureEnabled(project, feature) {
  return !!project.features?.[feature]?.enabled;
}

/**
 * @returns true if the search query is valid according to the backend requirements
 * @param search Search query to validate
 */
export function isValidProjectSearchQuery(search) {
  return search !== undefined && search.trim().length >= MIN_CHARACTERS_FOR_PROJECT_SEARCH;
}

/**
 * Mapping of sort fields to their corresponding date labels and
 * getter functions to extract the raw date value.
 * For non-date sort fields (name, size), falls back to "Last Updated" as the
 * most relevant date to display alongside the project.
 */
const PROJECT_DATE_FIELD_MAP = {
  created: {
    label: "Created",
    getValue: project => project.createdAt
  },
  lastCaptured: {
    label: "Last Captured",
    getValue: project => project.lastCapturedAt
  },
  lastUpdated: {
    label: "Last Updated",
    getValue: project => project.modifiedAt
  },
  name: {
    label: "Last Updated",
    getValue: project => project.modifiedAt
  },
  size: {
    label: "Last Updated",
    getValue: project => project.modifiedAt
  }
};

/**
 * Gets the date label based on the sort field.
 *
 * @param sortField The field by which projects are sorted.
 * @returns The label for the date field.
 */
export function getProjectDateLabel(sortField) {
  return PROJECT_DATE_FIELD_MAP[sortField].label;
}

/**
 * Gets the raw date value based on the sort field.
 *
 * @param project The project to get the date from.
 * @param sortField The field by which projects are sorted.
 * @returns The date or null if not available.
 */
export function getProjectDateBySort(project, sortField) {
  return PROJECT_DATE_FIELD_MAP[sortField].getValue(project);
}

/**
 * Compares two nullable numbers, treating null as the largest value
 * so that nulls sort naturally with direction (first in descending, last in ascending).
 * This matches the server-side project list behavior.
 */
function compareNullableNumbers(a, b, direction) {
  if (a === null && b === null) {
    return 0;
  }
  if (a === null) {
    return direction;
  }
  if (b === null) {
    return -direction;
  }
  return direction * (a - b);
}

/**
 * Explicit numeric sort direction for each API sort key.
 * "recently_modified_first/last" use semantic naming (not standard asc/desc),
 * so we map every key explicitly instead of deriving from `PROJECTS_SORT_BY_MAP.sort`.
 */
const SORT_DIRECTION = {
  ["name_ascending"]: 1,
  ["name_descending"]: -1,
  ["recently_modified_first"]: -1,
  ["recently_modified_last"]: 1,
  ["created_at_asc"]: 1,
  ["created_at_desc"]: -1,
  ["storage_asc"]: 1,
  ["storage_desc"]: -1,
  ["last_captured_at_asc"]: 1,
  ["last_captured_at_desc"]: -1
};

/** Sorts projects client-side by the given sort criteria */
export function sortProjects(projects, sortBy) {
  const {
    field
  } = PROJECTS_SORT_BY_MAP[sortBy];
  const direction = SORT_DIRECTION[sortBy];
  return [...projects].sort((a, b) => {
    if (field === "name") {
      return direction * a.name.localeCompare(b.name);
    }
    const valueA = field === "size" ? a.storage?.size ?? null : getProjectDateBySort(a, field);
    const valueB = field === "size" ? b.storage?.size ?? null : getProjectDateBySort(b, field);
    return compareNullableNumbers(valueA, valueB, direction);
  });
}